/**
 * AI-JAVADOC: WfProcessStepActivityUfFlowComponent
 * Module: WF (Workflow)
 * Type: Protected Component - Process Activity
 * Description: Workflow activity component that embeds a UF (Universal Forms) flow component.
 *              Starts or loads a UF flow based on the activity configuration (flowTypeDtoId),
 *              processes import bindings (startObject), and stores the completed flow result
 *              via WfProcessRuntimeService for the parent WF process to consume.
 */
import {Component, OnChanges, OnDestroy, OnInit} from '@angular/core';
import {
    WfProcessStepActivityBaseComponent
} from '../wf-process-step-activity-base/wf-process-step-activity-base.component';
import {WfProcessRuntimeBindingDto} from '../../../../model/dto/api/wf-process-runtime-binding.dto';
import {WfProcessRuntimeBindingUtil} from '../../../../logic/wf-process-runtime-binding.util';
import {WfProcessRuntimeService} from '../../../../service/wf-process-runtime.service';
import {WfActivityProcessingResultFieldDto} from '../../../../model/dto/api/wf-activity-processing-result-field.dto';
import {WfBindingFieldActionEnum} from '../../../../enum/wf-binding-field-action.enum';
import {WfProcessStepActivityEvent} from '../../../../logic/wf-process-step-activity.event';
import {UfFlowDto} from '../../../../../../feature-core/uf/model/dto/entity/uf-flow.dto';
import {UfFlowService} from '../../../../../../feature-core/uf/service/api/uf-flow/uf-flow.service';
import {UfFlowStartRequestDto} from '../../../../../../feature-core/uf/model/dto/api/uf-flow-start-request.dto';
import {ObjectIdentifier} from '../../../../../../core/shared/basic/object-identifier';
import {MvsCoreService} from '../../../../../../core/shared/service/mvs-core.service';
import {MvsMessageService} from '../../../../../../core/shared/service/message/mvs-message.service';

@Component({
    selector: 'mvs-wf-process-step-activity-uf-flow',
    templateUrl: './wf-process-step-activity-uf-flow.component.html',
    styleUrls: ['./wf-process-step-activity-uf-flow.component.scss'],
    standalone: false
})
export class WfProcessStepActivityUfFlowComponent
    extends WfProcessStepActivityBaseComponent
    implements OnInit, OnChanges, OnDestroy {

    ufFlowId: number;
    ufFlowObjectIdentifier: ObjectIdentifier;
    flowStartError: boolean = false;
    flowStartErrorMessage: string = '';
    flowCompleted: boolean = false;

    constructor(
        protected coreService: MvsCoreService,
        protected messageService: MvsMessageService,
        private flowService: UfFlowService) {

        super(coreService, messageService);
    }

    /**
     * Refresh Component.
     * Reads import bindings and starts a UF flow.
     */
    refreshComponent() {
        this.busy = true;
        this.flowStartError = false;
        this.flowStartErrorMessage = '';
        this.flowCompleted = false;

        // Extract startObject binding (if present)
        const startObjectBinding = WfProcessRuntimeBindingUtil.deriveBinding(this.bindings, 'startObject');

        // Determine flowTypeId from activity configuration
        const flowTypeId = this.activityType.activityUfFlowDto.flowTypeDtoId;

        // Build start request
        const startRequest: UfFlowStartRequestDto = {
            flowTypeId: flowTypeId,
            startObjectTypeId: startObjectBinding?.objectType?.id,
            startObjectId: startObjectBinding?.objectId,
        };

        this.flowService.start(startRequest).subscribe({
            next: (flowDto: UfFlowDto) => {
                this.ufFlowId = flowDto.id;
                this.ufFlowObjectIdentifier = new ObjectIdentifier('uf.UfFlow', this.ufFlowId);
                this.busy = false;
                this.initialized = true;
            },
            error: (error) => {
                this.flowStartError = true;
                this.flowStartErrorMessage = error?.error?.data || error?.error?.message || 'Unbekannter Fehler';
                this.busy = false;
                this.initialized = true;
            }
        });
    }

    /**
     * Handle flow completed event from the UF flow component.
     * Builds processing result and stores it via WfProcessRuntimeService.
     * @param flowDto The completed flow DTO.
     */
    onFlowCompleted(flowDto: UfFlowDto): void {
        this.flowCompleted = true;

        const results: WfActivityProcessingResultFieldDto[] = [];

        // Store the completed flow reference as result
        results.push({
            alias: 'ufFlowCompleted',
            action: WfBindingFieldActionEnum.created,
            valueObjectTypeAlias: 'uf.UfFlow',
            valueObjectId: this.ufFlowId
        });

        WfProcessRuntimeService.setResult(results);
    }

    /**
     * Handle flow cancelled event from the UF flow component.
     * If the activity is not mandatory, emits onSkip. Otherwise shows a message.
     * @param flowDto The cancelled flow DTO.
     */
    onFlowCancelled(flowDto: UfFlowDto): void {
        if (!this.activityType.activityUfFlowDto.mandatoryActivity) {
            this.onSkip.emit(new WfProcessStepActivityEvent(null, this.activityType));
        } else {
            this.messageService.showInfoMessage(
                'Pflichtablauf',
                'Dieser Ablauf ist ein Pflichtschritt und muss abgeschlossen werden.'
            );
        }
    }
}
